import { inject, Injectable } from '@angular/core';
import {
  HttpTransportType,
  HubConnection,
  HubConnectionBuilder,
  IHttpConnectionOptions,
  LogLevel,
} from '@microsoft/signalr';
import { Store } from '@ngxs/store';
import { ConfigService } from '@orion-platform/core';
import {
  GetNotifications,
  GetNotificationsUnreadCount,
} from 'apps/orion/src/app/features/scm/material-management/v1/store/supply-visibility/supply-visibility.actions';
import { Observable, Subject } from 'rxjs';
import { SignalrNotificationService } from './signalr-notification.service';

/** Hub event constants */
class ApplicationHubEvents {
  static readonly NotificationReceived = 'ReceiveNotification';
}

export type ReceiveNotificationMessage = {
  DateCreated: Date;
  Id: string;
  IsRead: boolean;
  ShortMessage: string;
  Title: string;
};

@Injectable({ providedIn: 'root' })
export class SignalRService {
  private readonly configService = inject(ConfigService);
  private readonly signalrNotificationService = inject(SignalrNotificationService);
  private readonly store = inject(Store);

  private appHubConnection?: HubConnection;
  public isAppHubConnected = false;

  private readonly isTokenReady$ = new Subject<boolean>();

  getTokenReady(): Observable<boolean> {
    return this.isTokenReady$.asObservable();
  }

  setTokenReady(isTokenReady: boolean): void {
    this.isTokenReady$.next(isTokenReady);
  }

  initSignalRService(token: string): void {
    if (this.isAppHubConnected) return;

    try {
      const url = this.getSignalREndpoint();
      const signalRConfig = this.getSignalRConfig(token);

      this.appHubConnection = new HubConnectionBuilder()
        .withUrl(url, signalRConfig)
        .configureLogging(LogLevel.None)
        .withAutomaticReconnect()
        .build();

      this.registerOnAppServerEvents();
      this.startAppHubConnection();
      this.onCloseAppHubConnection();
    } catch (error) {
      console.error('Error initializing SignalR connection', error);
    }
  }

  reconnectSignalRService(token: string): void {
    if (!this.appHubConnection) return;

    this.appHubConnection
      .stop()
      .then((): void => {
        this.isAppHubConnected = false;
        this.initSignalRService(token);
      })
      .catch((error): void => console.error('Error on reconnecting SignalR', error));
  }

  private registerOnAppServerEvents(): void {
    if (!this.appHubConnection) return;

    try {
      this.appHubConnection.on(
        ApplicationHubEvents.NotificationReceived,
        (userId: string, message: ReceiveNotificationMessage | string): void => {
          const tMessage =
            typeof message === 'string'
              ? (JSON.parse(message) as ReceiveNotificationMessage)
              : message;

          this.signalrNotificationService.showNotification(tMessage.Title, tMessage.ShortMessage);

          // load new notifications
          this.store.dispatch(new GetNotifications());
          this.store.dispatch(new GetNotificationsUnreadCount());
          // this.store.dispatch(new GetUnreadCount());
        },
      );
      //IMPLEMENT Other Events
    } catch (error) {
      console.error('Error on receiving notifications', error);
    }
  }

  private startAppHubConnection(): void {
    if (!this.appHubConnection || this.isAppHubConnected) return;

    this.appHubConnection
      .start()
      .then((): void => {
        console.log('Connection to signalR has been established....');
        this.isAppHubConnected = true;
      })
      .catch((): void => {
        console.log('signalR unable to establish a connection...');
        this.isAppHubConnected = false;
      });
  }

  private onCloseAppHubConnection(): void {
    if (!this.appHubConnection) return;

    this.appHubConnection.onclose((error?: Error): void => {
      if (error) console.error('SignalR onClose Error', error);
    });
  }

  private getSignalREndpoint(): string {
    return `${this.configService.getSettings('signalRHub')}`;
  }

  private getSignalRConfig(token: string): IHttpConnectionOptions {
    return {
      accessTokenFactory: (): string => token,
      skipNegotiation: true,
      transport: HttpTransportType.WebSockets,
    };
  }
}
